import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { User, ShieldCheck, KeyRound, Loader2, Coins, Calendar, Mail, BadgeCheck, Bell, Link2, Trash2, AlertTriangle, MonitorSmartphone } from "lucide-react";
import { FaYoutube, FaTiktok } from "react-icons/fa";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en", { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return "—";
  }
}

export default function Settings() {
  const { user, updateUser, logout } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState(user?.name || "");
  const [savingName, setSavingName] = useState(false);

  const [delOpen, setDelOpen] = useState(false);
  const [delPassword, setDelPassword] = useState("");
  const [deleting, setDeleting] = useState(false);

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [savingPw, setSavingPw] = useState(false);
  const [signOutAll, setSignOutAll] = useState(false);
  const [sessions, setSessions] = useState([]);

  useEffect(() => {
    api.get("/auth/sessions").then((r) => setSessions(r.data.sessions || [])).catch(() => {});
  }, []);

  const [emailNotif, setEmailNotif] = useState(user?.email_notifications ?? true);
  const [savingNotif, setSavingNotif] = useState(false);

  const [channels, setChannels] = useState({ youtube: "", tiktok: "", instagram: "" });
  const [savingChannels, setSavingChannels] = useState(false);
  const [yt, setYt] = useState({ connected: false, channel_title: "" });

  const loadYt = () => api.get("/youtube/status").then((r) => setYt(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/channels").then((r) => setChannels({ youtube: "", tiktok: "", instagram: "", ...r.data })).catch(() => {});
    loadYt();
    const p = new URLSearchParams(window.location.search).get("youtube");
    if (p === "connected") {
      toast.success("YouTube channel connected!");
      window.history.replaceState({}, "", "/settings");
    } else if (p === "error") {
      toast.error("Couldn't connect YouTube. Please try again.");
      window.history.replaceState({}, "", "/settings");
    }
  }, []);

  const connectYouTube = async () => {
    // Open a top-level tab first (Google blocks loading inside the preview iframe).
    const authWindow = window.open("about:blank", "_blank");
    try {
      const { data } = await api.get("/youtube/connect");
      if (authWindow) {
        authWindow.location.href = data.url;
      } else {
        // Popup blocked: navigate the current tab (works when app is opened in its own full tab).
        window.location.href = data.url;
      }
      // Poll for connection while the user completes Google sign-in in the new tab.
      const started = Date.now();
      const poll = setInterval(async () => {
        if (Date.now() - started > 180000) { clearInterval(poll); return; }
        try {
          const { data: s } = await api.get("/youtube/status");
          if (s.connected) {
            clearInterval(poll);
            setYt(s);
            toast.success(`YouTube connected${s.channel_title ? `: ${s.channel_title}` : ""}!`);
            if (authWindow && !authWindow.closed) authWindow.close();
          }
        } catch { /* transient error while polling — keep trying */ }
      }, 3000);
    } catch (err) {
      if (authWindow) authWindow.close();
      toast.error(apiError(err));
    }
  };

  const disconnectYouTube = async () => {
    try {
      await api.delete("/youtube/disconnect");
      setYt({ connected: false, channel_title: "" });
      toast.success("YouTube disconnected");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const saveChannels = async () => {
    setSavingChannels(true);
    try {
      const { data } = await api.put("/channels", channels);
      setChannels({ youtube: "", tiktok: "", instagram: "", ...data.channels });
      toast.success("Channels saved");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSavingChannels(false);
    }
  };

  const toggleNotif = async (value) => {
    setEmailNotif(value);
    setSavingNotif(true);
    try {
      await api.put("/auth/notifications", { email_notifications: value });
      updateUser({ email_notifications: value });
      toast.success(value ? "Email notifications on" : "Email notifications off");
    } catch (err) {
      setEmailNotif(!value); // revert on failure
      toast.error(apiError(err));
    } finally {
      setSavingNotif(false);
    }
  };

  const saveName = async () => {
    if (!name.trim()) {
      toast.error("Name cannot be empty");
      return;
    }
    setSavingName(true);
    try {
      const { data } = await api.put("/auth/profile", { name: name.trim() });
      updateUser({ name: data.name });
      toast.success("Profile updated");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSavingName(false);
    }
  };

  const changePassword = async () => {
    if (!current || !next) {
      toast.error("Fill in both password fields");
      return;
    }
    setSavingPw(true);
    try {
      const { data } = await api.put("/auth/password", { current_password: current, new_password: next, sign_out_all: signOutAll });
      if (data.token) localStorage.setItem("rf_token", data.token);
      toast.success(signOutAll ? "Password changed — all other devices signed out" : "Password changed");
      setCurrent("");
      setNext("");
      setSignOutAll(false);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSavingPw(false);
    }
  };

  const deleteAccount = async () => {
    if (!delPassword) {
      toast.error("Enter your password to confirm");
      return;
    }
    setDeleting(true);
    try {
      await api.delete("/auth/account", { data: { password: delPassword } });
      toast.success("Your account and all its content have been deleted.");
      logout();
      navigate("/");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-8 max-w-3xl" data-testid="settings-page">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">Account</p>
        <h1 className="font-head text-3xl font-bold tracking-tight">Settings</h1>
      </div>

      {/* Profile */}
      <Card icon={User} title="Profile">
        <div className="space-y-4">
          <div>
            <label className="block text-xs uppercase tracking-[0.15em] text-zinc-500 mb-1.5">Display name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="settings-name-input"
              className="w-full bg-[#0A0A0A] border border-white/10 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <button
            onClick={saveName}
            disabled={savingName}
            data-testid="settings-save-name-btn"
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 px-4 py-2 rounded-md text-sm font-semibold transition-colors disabled:opacity-60"
          >
            {savingName && <Loader2 className="h-4 w-4 animate-spin" />} Save changes
          </button>
        </div>
      </Card>

      {/* Account info */}
      <Card icon={BadgeCheck} title="Account details">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Info icon={Mail} label="Email" value={user?.email} />
          <Info icon={ShieldCheck} label="Role" value={user?.role} className="capitalize" />
          <Info icon={BadgeCheck} label="Plan" value={user?.plan || "free"} className="capitalize" />
          <Info icon={Coins} label="Credits" value={user?.credits} />
          <Info icon={Calendar} label="Member since" value={fmtDate(user?.created_at)} />
        </div>
      </Card>

      {/* Connected channels */}
      <Card icon={Link2} title="Connected channels">
        <p className="text-sm text-zinc-400 mb-4">
          Add the link to each channel you post to. These appear on your schedule so you can jump straight to the
          right channel when publishing.
        </p>
        <div className="space-y-3">
          <ChannelField icon={FaTiktok} label="TikTok" placeholder="https://tiktok.com/@yourhandle"
            value={channels.tiktok} onChange={(v) => setChannels((c) => ({ ...c, tiktok: v }))} testid="channel-tiktok" />
          <div>
            <ChannelField icon={FaYoutube} label="YouTube" placeholder="https://youtube.com/@yourchannel"
              value={channels.youtube} onChange={(v) => setChannels((c) => ({ ...c, youtube: v }))} testid="channel-youtube" />
            <div className="mt-2 ml-8 flex items-center gap-3">
              {yt.connected ? (
                <>
                  <span className="text-[12px] text-green-400 inline-flex items-center gap-1.5" data-testid="youtube-connected">
                    <BadgeCheck className="h-3.5 w-3.5" /> Connected{yt.channel_title ? ` · ${yt.channel_title}` : ""} — auto-upload enabled
                  </span>
                  <button onClick={disconnectYouTube} data-testid="youtube-disconnect-btn"
                    className="text-[12px] text-zinc-400 hover:text-red-400 underline">Disconnect</button>
                </>
              ) : (
                <button onClick={connectYouTube} data-testid="youtube-connect-btn"
                  className="inline-flex items-center gap-2 bg-red-600/90 hover:bg-red-600 px-3 py-1.5 rounded-md text-[12px] font-semibold transition-colors">
                  <FaYoutube className="h-3.5 w-3.5" /> Connect YouTube for auto-upload
                </button>
              )}
            </div>
          </div>
        </div>
        <button
          onClick={saveChannels}
          disabled={savingChannels}
          data-testid="settings-save-channels-btn"
          className="mt-4 inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 px-4 py-2 rounded-md text-sm font-semibold transition-colors disabled:opacity-60"
        >
          {savingChannels && <Loader2 className="h-4 w-4 animate-spin" />} Save channels
        </button>
      </Card>

      {/* Email notifications */}
      <Card icon={Bell} title="Email notifications">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="text-sm text-zinc-200">Receive notification emails</p>
            <p className="text-xs text-zinc-500 mt-0.5">
              Get an email when a video is scheduled or a post is published. Turn off anytime.
            </p>
          </div>
          <Switch
            checked={emailNotif}
            onCheckedChange={toggleNotif}
            disabled={savingNotif}
            data-testid="settings-email-notifications-switch"
            className="data-[state=checked]:bg-blue-600 shrink-0"
          />
        </div>
      </Card>

      {/* Policy acceptance */}
      <Card icon={ShieldCheck} title="Content & Copyright Policy">
        {user?.accepted_policy ? (
          <div className="flex items-center gap-3" data-testid="policy-status">
            <span className="inline-flex items-center gap-1.5 text-sm text-green-400">
              <BadgeCheck className="h-4 w-4" /> Accepted
            </span>
            <span className="text-sm text-zinc-500">on {fmtDate(user?.policy_accepted_at)}</span>
          </div>
        ) : (
          <p className="text-sm text-zinc-400" data-testid="policy-status">
            No acceptance recorded for this account.
          </p>
        )}
        <Link to="/legal" className="inline-block mt-3 text-sm text-blue-400 hover:text-blue-300 underline">
          Read the policy
        </Link>
      </Card>

      {/* Security */}
      <Card icon={KeyRound} title="Change password">
        <div className="space-y-4">
          <div>
            <label className="block text-xs uppercase tracking-[0.15em] text-zinc-500 mb-1.5">Current password</label>
            <input
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              data-testid="settings-current-password"
              className="w-full bg-[#0A0A0A] border border-white/10 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.15em] text-zinc-500 mb-1.5">New password</label>
            <input
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              data-testid="settings-new-password"
              className="w-full bg-[#0A0A0A] border border-white/10 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <label className="flex items-center gap-2.5 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={signOutAll}
              onChange={(e) => setSignOutAll(e.target.checked)}
              data-testid="settings-signout-all-checkbox"
              className="h-4 w-4 rounded border-white/20 bg-[#0A0A0A] accent-blue-600"
            />
            <span className="text-sm text-zinc-400">Also sign out of all other devices</span>
          </label>
          <button
            onClick={changePassword}
            disabled={savingPw}
            data-testid="settings-change-password-btn"
            className="inline-flex items-center gap-2 bg-white/5 border border-white/10 hover:bg-white/10 px-4 py-2 rounded-md text-sm font-semibold transition-colors disabled:opacity-60"
          >
            {savingPw && <Loader2 className="h-4 w-4 animate-spin" />} Update password
          </button>
        </div>
      </Card>

      {/* Active sessions */}
      <Card icon={MonitorSmartphone} title="Active sessions">
        <p className="text-sm text-zinc-400 mb-4">
          Devices signed in to your account. See something you don't recognize? Change your password above and tick
          "sign out of all other devices".
        </p>
        <div className="space-y-2" data-testid="settings-sessions-list">
          {sessions.map((s, i) => (
            <div key={s.id} data-testid={`session-row-${i}`} className="flex items-center justify-between gap-3 border border-white/10 rounded-md bg-[#0A0A0A] px-4 py-3">
              <div className="min-w-0">
                <p className="text-sm text-zinc-200 flex items-center gap-2">
                  {s.device}
                  {s.current && (
                    <span data-testid="session-current-badge" className="text-[10px] uppercase tracking-wider font-bold text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 rounded px-1.5 py-0.5">
                      This device
                    </span>
                  )}
                </p>
                <p className="text-xs text-zinc-600 mt-0.5">
                  {s.created_at ? `Signed in ${fmtDate(s.created_at)}` : "Current session"}{s.ip ? ` · ${s.ip}` : ""}
                </p>
              </div>
            </div>
          ))}
          {sessions.length === 0 && <p className="text-sm text-zinc-600" data-testid="sessions-empty">No session data yet.</p>}
        </div>
      </Card>

      {/* Danger zone */}
      <Card icon={AlertTriangle} title="Delete account">
        <p className="text-sm text-zinc-400 mb-4">
          Permanently delete your account and everything in it — videos, voiceovers, visuals, series and scheduled posts.
          This cannot be undone.
        </p>
        <button
          onClick={() => { setDelPassword(""); setDelOpen(true); }}
          data-testid="settings-delete-account-btn"
          className="inline-flex items-center gap-2 bg-red-600/90 hover:bg-red-600 px-4 py-2 rounded-md text-sm font-semibold transition-colors"
        >
          <Trash2 className="h-4 w-4" /> Delete my account
        </button>
      </Card>

      <Dialog open={delOpen} onOpenChange={(o) => !o && setDelOpen(false)}>
        <DialogContent className="bg-[#121212] border-white/10 text-white" data-testid="delete-account-dialog">
          <DialogHeader>
            <DialogTitle className="font-head flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-400" /> Delete your account?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-zinc-400">
              This is permanent. All your videos, voiceovers, visuals, series and scheduled posts will be
              erased and cannot be recovered. Enter your password to confirm.
            </p>
            <input
              type="password"
              value={delPassword}
              onChange={(e) => setDelPassword(e.target.value)}
              placeholder="Your password"
              data-testid="delete-account-password"
              className="w-full bg-[#0A0A0A] border border-white/10 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setDelOpen(false)}
                data-testid="delete-account-cancel"
                className="px-4 py-2 rounded-md text-sm font-semibold bg-white/5 border border-white/10 hover:bg-white/10 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={deleteAccount}
                disabled={deleting}
                data-testid="delete-account-confirm"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold bg-red-600 hover:bg-red-500 transition-colors disabled:opacity-60"
              >
                {deleting && <Loader2 className="h-4 w-4 animate-spin" />} Permanently delete
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Card({ icon: Icon, title, children }) {
  return (
    <div className="border border-white/10 rounded-md bg-[#121212] p-6">
      <h2 className="font-head text-lg font-semibold mb-4 flex items-center gap-2">
        <Icon className="h-4 w-4 text-blue-400" /> {title}
      </h2>
      {children}
    </div>
  );
}

function Info({ icon: Icon, label, value, className = "" }) {
  return (
    <div className="border border-white/10 rounded-sm bg-[#0A0A0A] p-4">
      <p className="text-xs text-zinc-500 mb-1 flex items-center gap-1.5">
        <Icon className="h-3.5 w-3.5" /> {label}
      </p>
      <p className={`text-sm text-zinc-200 truncate ${className}`}>{value ?? "—"}</p>
    </div>
  );
}

function ChannelField({ icon: Icon, label, placeholder, value, onChange, testid }) {
  return (
    <div className="flex items-center gap-3">
      <Icon className="h-5 w-5 text-zinc-400 shrink-0" />
      <input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        aria-label={`${label} channel link`}
        className="flex-1 bg-[#0A0A0A] border border-white/10 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
    </div>
  );
}
